import React, { useContext, useEffect, useMemo, useState } from "react";
import _ from "lodash";
import cx from 'classnames';
import { Assessment, Course, MultipleChoiceQuestion, Option, Question, TextQuestion } from "src/types/coaching/courses";

import styles from "./assessment.module.scss";
import { useDispatch } from "react-redux";
import { createQuestionInAssessment, deleteQuestionInAssessment, rearrangeQuestionsInAssessment, updateAssessmentProperty, updateQuestionInAssessment } from "src/redux/actions/coaching/courses";
import { ScrollableContainer } from "src/components/scroller";
import { animated, Spring, useSpring } from "@react-spring/web";
import { Dropdown } from "react-bootstrap";
import { nanoid } from "nanoid";
import Tooltip from "src/components/tooltip";
import { DndContext, PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import { SortableContext, useSortable, verticalListSortingStrategy } from "@dnd-kit/sortable";
import { CSS } from '@dnd-kit/utilities';
import Button from "src/components/button";
import TextareaAutosize from "react-autosize-textarea";
import { CourseContext } from "../../../context";
import useMeasure from "react-use-measure";
import { QUESTION_TYPES } from "src/constants/coaching";
import { useSelector } from "react-redux";
import Reveal from "src/components/animation";

interface EditAssessmentContentProps {
  course: Course;
  assessment: Assessment;
}

export const EditAssessmentContent: React.FC<EditAssessmentContentProps> = ({ course, assessment }) => {
  const dispatch = useDispatch();

  const courseContext = useContext(CourseContext);

  const { layout } = useMemo(() => {
    return {
      layout: _.get(courseContext, 'preferences.assessment_layout') || 'grid'
    };
  }, [courseContext]);

  const name = useMemo(() => {
    return _.get(assessment, "name");
  }, [assessment]);

  const nameError = useSelector((state: any) => {
    const errors = _.get(state.courses, `edit.errors`) || [];
    return _.find(errors, { type: 'assessment', uuid: assessment.uuid, property: 'name' });
  });

  const handlePropertyChange = (property: string, value: any) => {
    dispatch(updateAssessmentProperty({
      course: course.id!,
      assessment: assessment.uuid,
      property,
      value
    }));
  }

  const handleNameChange = (e: any) => {
    handlePropertyChange("name", e.target.value);
  }

  const handleCreateQuestion = (index) => {
    const answer = nanoid(6);
    const question = {
      uuid: nanoid(6),
      name: "Question",
      type: "multiple_choice",
      options: [{
        uuid: answer,
        name: "Option 1",
        order: 0
      }, {
        uuid: nanoid(6),
        name: "Option 2",
        order: 1
      }, {
        uuid: nanoid(6),
        name: "Option 3",
        order: 2
      }, {
        uuid: nanoid(6),
        name: "Option 4",
        order: 3
      }],
      answer: answer,
      order: index
    } as MultipleChoiceQuestion;

    dispatch(createQuestionInAssessment({
      course: course.id!,
      assessment: assessment.uuid,
      request: question,
      insertAt: index
    }));
  }

  const handleQuestionChange = (question: Question) => {
    dispatch(updateQuestionInAssessment({
      course: course.id!,
      assessment: assessment.uuid,
      request: question as MultipleChoiceQuestion | TextQuestion
    }))
  }

  const handleDeleteQuestion = (question: Question) => {
    console.log("Delete Question", question);
    dispatch(deleteQuestionInAssessment({
      course: course.id!,
      assessment: assessment.uuid,
      question: question.uuid
    }));
  }

  return (
    <div className={styles.editContainer}>
      <div className={styles.header}>
        <input className={styles.name}
          value={name}
          placeholder="Enter Assessment name..."
          onChange={handleNameChange}
        />
        <Reveal show={nameError != null}>
          <div className={styles.error}>
            <i className="fa-solid fa-triangle-exclamation"></i>
            <span>{nameError?.message}</span>
          </div>
        </Reveal>
      </div>
      <div className={styles.questionsContainer}>
        {layout == 'grid' ?
          <QuestionTabsContainer
            assessment={assessment}
            onCreateClicked={handleCreateQuestion}
            onQuestionChange={handleQuestionChange}
            onDelete={handleDeleteQuestion}
          /> : <QuestionAccordianContainer
            course={course}
            assessment={assessment}
            onCreateClicked={handleCreateQuestion}
            onQuestionChange={handleQuestionChange}
            onDelete={handleDeleteQuestion}
          />}
      </div>
    </div>
  );
}

interface QuestionTabsContainerProps {
  assessment: Assessment;
  // eslint-disable-next-line no-unused-vars
  onCreateClicked: (index: number) => void;
  // eslint-disable-next-line no-unused-vars
  onQuestionChange: (question: Question) => void;
  // eslint-disable-next-line no-unused-vars
  onDelete: (question: Question) => void;
}

const QuestionTabsContainer: React.FC<QuestionTabsContainerProps> = ({ assessment, onCreateClicked, onQuestionChange, onDelete }) => {
  const [activeTab, setActiveTab] = useState(0);

  const courseContext = useContext(CourseContext);

  const questions = useMemo(() => {
    return _.get(assessment, "questions") || [];
  }, [assessment]);

  const errors = useSelector((state: any) => {
    return _.get(state.courses, `edit.errors`) || [];
  });

  const handleCreateQuestion = (index) => {
    onCreateClicked(index);
    setActiveTab(index);
  }

  const handleNavigateToQuestion = (index: number) => {
    setActiveTab(index);
  }

  useEffect(() => {
    setActiveTab(0);
  }, [assessment?.uuid]);

  useEffect(() => {
    if (courseContext.validationError) {
      const type = courseContext.validationError.type;
      const uuid = courseContext.validationError.uuid;

      if (type === 'assessment' && uuid === assessment.uuid) {
        const question = courseContext.validationError.additionalData?.question;
        if (question) {
          const index = _.findIndex(questions, { uuid: question });
          setActiveTab(index);
        }

        courseContext.gotoError(undefined);
      }
    }
  }, [courseContext.validationError]);

  return (
    <div className={styles.questionTabsContainer}>
      <div className={styles.tabs}>
        <div className={styles.tabsScroller}>
          <ScrollableContainer distance={100}>
            {_.map(questions, (question, index) => {

              const hasError = _.find(errors, { type: 'assessment', uuid: assessment.uuid, additionalData: { question: question.uuid } }) != null;

              return (
                <div key={index} className={cx(styles.tab, {
                  [styles.error]: hasError,
                  [styles.active]: index === activeTab
                })} onClick={() => setActiveTab(index)}>
                  <span className={styles.index}>
                    {index + 1}
                  </span>
                </div>
              )
            })}
          </ScrollableContainer>
        </div>
        <div className={styles.addTab} onClick={() => handleCreateQuestion(activeTab + 1)}>
          <i className="paperflite-add"></i>
        </div>
      </div>
      <div className={styles.tabsContainer}>
        {_.map(questions, (question, index) => (
          <QuestionTabContainer key={index}
            show={index === activeTab}
            assessment={assessment}
            question={question}
            onCreateClicked={() => handleCreateQuestion(index + 1)}
            onChange={onQuestionChange}
            onDelete={onDelete}
            navigateToQuestion={handleNavigateToQuestion}
          />
        ))}
      </div>
    </div>
  );
}

interface QuestionTabContainerProps {
  show: boolean;
  assessment: Assessment;
  question: Question;
  // eslint-disable-next-line no-unused-vars
  onCreateClicked: (index: number) => void;
  // eslint-disable-next-line no-unused-vars
  onChange: (question: Question) => void;
  // eslint-disable-next-line no-unused-vars
  onDelete: (question: Question) => void;
  // eslint-disable-next-line no-unused-vars
  navigateToQuestion: (index: number) => void;
}

const QuestionTabContainer: React.FC<QuestionTabContainerProps> = ({ show, assessment, question, onCreateClicked, onChange, onDelete, navigateToQuestion }) => {
  const [mounted, setMounted] = React.useState(true);
  const [hidden, setHidden] = React.useState(false);

  const [animation] = useSpring({
    opacity: show ? 1 : 0,
    transform: show ? 'translate3d(0,0,0)' : 'translate3d(0, 200px, 0)',
    onRest: () => {
      const unmount = !show;
      if (unmount) {
        setMounted(!unmount);
      }

      setHidden(show);
    }
  }, [show]);

  useEffect(() => {
    if (show) {
      setMounted(show);
    }

    if (show) {
      setHidden(!show);
    }
  }, [show]);

  return (
    <animated.div style={animation} className={cx(styles.questionTabContainer, {
      [styles.active]: show,
      [styles.hidden]: hidden
    })}>
      {mounted &&
        <QuestionContainer
          assessment={assessment}
          question={question}
          onCreateClicked={onCreateClicked}
          onChange={onChange}
          onDelete={onDelete}
          navigateToQuestion={navigateToQuestion}
        />
      }
    </animated.div>
  );
}

interface QuestionAccordianContainerProps {
  course: Course;
  assessment: Assessment;
  // eslint-disable-next-line no-unused-vars
  onCreateClicked: (index: number) => void;
  // eslint-disable-next-line no-unused-vars
  onQuestionChange: (question: Question) => void;
  // eslint-disable-next-line no-unused-vars
  onDelete: (question: Question) => void;
}

const QuestionAccordianContainer: React.FC<QuestionAccordianContainerProps> = ({ course, assessment, onCreateClicked, onDelete, onQuestionChange }) => {
  const dispatch = useDispatch();

  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: {
      distance: 8
    }
  });

  const sensors = useSensors(
    pointerSensor
  );

  const questions = useMemo(() => {
    return _.get(assessment, "questions") || [];
  }, [assessment]);

  const handleCreateQuestion = (index) => {
    onCreateClicked(index);
  }

  const handleDragFinish = (event: any) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const source = active.data.current.entity;
      const target = over.data.current.entity;

      dispatch(rearrangeQuestionsInAssessment({
        course: course.id!,
        assessment: assessment.uuid,
        source: source.uuid,
        target: target.uuid
      }));
    }
  }

  return (
    <div className={styles.questionAccordianContainer}>
      <div className={styles.totalQuestions}>
        Total no.of Questions: {_.padStart(String(_.size(questions)), 2, '0')}
      </div>
      <div className={styles.panelContainer}>
        <div className={styles.questions}>
          <DndContext sensors={sensors} modifiers={[restrictToVerticalAxis]} onDragOver={handleDragFinish}>
            <SortableContext id={"questions_list"} items={questions.map(q => q.uuid)} strategy={verticalListSortingStrategy}>
              {_.map(questions, (question, index) => (
                <QuestionAccordianPanel key={index}
                  index={index}
                  assessment={assessment}
                  question={question}
                  onCreateClicked={() => handleCreateQuestion(index + 1)}
                  onChange={onQuestionChange}
                  onDelete={onDelete}
                />
              ))}
            </SortableContext>
          </DndContext>
          <div className={styles.newQuestion} onClick={() => handleCreateQuestion(_.size(questions))}>
            <Button variant="tertiary" icon="paperflite-add">
              Add New Question
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

interface QuestionAccordianPanelProps {
  index: number;
  assessment: Assessment;
  question: Question;
  // eslint-disable-next-line no-unused-vars
  onCreateClicked: (index: number) => void;
  // eslint-disable-next-line no-unused-vars
  onChange: (question: Question) => void;
  // eslint-disable-next-line no-unused-vars
  onDelete: (question: Question) => void;
}

const QuestionAccordianPanel: React.FC<QuestionAccordianPanelProps> = ({ index, assessment, question, onCreateClicked, onChange, onDelete }) => {
  const courseContext = useContext(CourseContext);
  const [expanded, setExpanded] = useState(false);

  const [dimensions, setDimensions] = React.useState<{ height: number | string }>({
    height: 0,
  });

  const questionType = useMemo(() => {
    const type = _.get(question, "type");
    return QUESTION_TYPES.find((t) => t.id === type)?.value || "NA";
  }, [question]);

  const [expandedPanelRef, expandedPanelBounds] = useMeasure();
  const [collapsedPanelRef, collapsedPanelBounds] = useMeasure();

  const {
    attributes,
    listeners,
    setNodeRef,
    isDragging,
    transform,
    transition,
  } = useSortable({
    id: question!.uuid || 'new-question',
    disabled: expanded,
    data: {
      entity: question,
      type: 'question'
    }
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  useEffect(() => {
    let height: any = 0;
    if (expanded) {
      height = (expandedPanelBounds.height || 0) + 40;
    } else {
      height = (collapsedPanelBounds.height || 0) + 40;
    }

    setDimensions({
      height: height
    });

  }, [expandedPanelBounds, collapsedPanelBounds, expanded]);

  useEffect(() => {
    if (courseContext.validationError) {
      const type = courseContext.validationError.type;
      const uuid = courseContext.validationError.uuid;

      if (type === 'assessment' && uuid === assessment.uuid) {
        const questionId = courseContext.validationError.additionalData?.question;
        if (question.uuid === questionId) {
          setExpanded(true);
          courseContext.gotoError(undefined);
        }
      }
    }
  }, [courseContext.validationError]);

  return (
    <Spring to={{ height: dimensions.height }} config={{
      friction: 14,
      tension: 120,
      bounce: 1,
    }}>
      {props => (
        <animated.div style={{ ...props, ...style }}
          className={cx(styles.questionAccordianPanel, {
            [styles.dragging]: isDragging
          })}
          ref={setNodeRef} {...listeners} {...attributes}
        >
          <div ref={collapsedPanelRef} className={cx(styles.body, styles.collapsed, {
            [styles.show]: !expanded
          })} onClick={() => setExpanded(true)}>
            <div className={styles.index}>{index + 1}</div>
            <div className={styles.question}>
              <div className={styles.name}>
                {question.name}
              </div>
              <div className={styles.type}>
                Question Type: {questionType}
              </div>
            </div>
            <div className={styles.actions}>
              <button className={cx(styles.action, styles.expand)}>
                <i className="paperflite-dropdown"></i>
              </button>
            </div>
          </div>
          <div ref={expandedPanelRef} className={cx(styles.body, styles.expanded, {
            [styles.show]: expanded
          })}>
            <div className={styles.header}>
              <div className={styles.index}>Question {index + 1}</div>
              <div className={styles.actions}>
                <button className={cx(styles.action, styles.collapse)} onClick={() => setExpanded(false)}>
                  <i className="paperflite-dropdown"></i>
                </button>
              </div>
            </div>
            <QuestionContainer
              assessment={assessment}
              question={question}
              onCreateClicked={onCreateClicked}
              onChange={onChange}
              onDelete={onDelete}
              navigateToQuestion={() => { }}
            />
          </div>
        </animated.div>
      )}
    </Spring>
  );
}

interface QuestionContainerProps {
  assessment: Assessment;
  question: Question;
  // eslint-disable-next-line no-unused-vars
  onCreateClicked: (index: number) => void;
  // eslint-disable-next-line no-unused-vars
  onChange: (question: Question) => void;
  // eslint-disable-next-line no-unused-vars
  onDelete: (question: Question) => void;
  // eslint-disable-next-line no-unused-vars
  navigateToQuestion: (index: number) => void;
}

const QuestionContainer: React.FC<QuestionContainerProps> = ({ assessment, question, onCreateClicked, onChange, onDelete, navigateToQuestion }) => {

  const name = useMemo(() => {
    return _.get(question, "name");
  }, [question]);

  const answerType = useMemo(() => {
    return _.get(question, "type");
  }, [question]);

  const index = useMemo(() => {
    return _.findIndex(assessment.questions, (q) => q.uuid === question.uuid);
  }, [assessment, question]);

  const questionTypes = useMemo(() => {
    return QUESTION_TYPES.map((type) => {
      return {
        label: type.value,
        value: type.id
      }
    });
  }, []);

  const nameError = useSelector((state: any) => {
    const errors = _.get(state.courses, `edit.errors`) || [];
    const property = `questions.${question.uuid}.name`;
    return _.find(errors, { type: 'assessment', uuid: assessment.uuid, property: property });
  });

  const answerError = useSelector((state: any) => {
    const errors = _.get(state.courses, `edit.errors`) || [];
    const property = `questions.${question.uuid}.answer`;
    return _.find(errors, { type: 'assessment', uuid: assessment.uuid, property: property });
  });

  const optionsError = useSelector((state: any) => {
    const errors = _.get(state.courses, `edit.errors`) || [];
    const property = `questions.${question.uuid}.options`;
    return _.find(errors, { type: 'assessment', uuid: assessment.uuid, property: property });
  });

  const handleQuestionChange = (request: Question) => {
    console.log("Question Change", request);
    onChange(request);
  }

  const handleNameChange = (e: any) => {
    handleQuestionChange({
      ...question,
      name: e.target.value
    });
  }

  const handleTypeChange = (value: string) => {
    let request: any = {
      type: value as Question['type']
    }

    switch (value) {
      case "multiple_choice": {
        request = {
          ...request,
          options: [{
            uuid: nanoid(6),
            name: "",
            order: 0
          }, {
            uuid: nanoid(6),
            name: "",
            order: 1
          }, {
            uuid: nanoid(6),
            name: "",
            order: 2
          }],
          answer: ""
        }
        break;
      }
      case "true_false": {
        request = {
          ...request,
          options: [{
            uuid: nanoid(6),
            name: "True",
            order: 0
          }, {
            uuid: nanoid(6),
            name: "False",
            order: 1
          }],
          answer: ""
        }

        break;
      }
      case "text": {
        request = {
          ...request,
          answer: ""
        }
        break;
      }
    }

    handleQuestionChange({
      ...question,
      ...request
    });
  }

  const handleDeleteQuestion = () => {
    onDelete(question);

    const nextIndex = Math.max(0, index - 1);
    navigateToQuestion(nextIndex);
  }

  const handlePreviousClicked = () => {
    if (index > 0) {
      navigateToQuestion(index - 1);
    }
  }

  const handleNextClicked = () => {
    if (index < _.size(assessment.questions)) {
      navigateToQuestion(index + 1);
    }
  }

  return (
    <div className={cx(styles.questionContainer)}>
      <div className={styles.nameField}>
        <TextareaAutosize className={styles.nameInput}
          placeholder="Enter Question..."
          rows={1}
          maxRows={10}
          value={name}
          onChange={handleNameChange}
          onPointerEnterCapture={() => { }}
          onPointerLeaveCapture={() => { }}
        />
        <Reveal className={styles.error} show={nameError != null}>
          <i className="fa-solid fa-triangle-exclamation"></i>
          <span>Question cannot be empty</span>
        </Reveal>
      </div>
      <div className={styles.typeField}>
        <label>Answer Type</label>
        <SelectMenu
          selected={answerType}
          placeholder="Select Type"
          options={questionTypes}
          onChange={handleTypeChange}
        />
      </div>
      <div className={styles.answerField}>
        <Reveal className={styles.error} show={answerError != null}>
          <i className="fa-solid fa-triangle-exclamation"></i>
          <span>Enter a valid answer</span>
        </Reveal>
        <Reveal className={styles.error} show={optionsError != null}>
          <i className="fa-solid fa-triangle-exclamation"></i>
          <span>Two or more choices are required</span>
        </Reveal>
        {answerType === "multiple_choice" && (
          <MultipleChoiceQuestionContainer canCreateOption={true} assessment={assessment} question={question as MultipleChoiceQuestion} onChange={handleQuestionChange} />
        )}
        {answerType === "true_false" && (
          <MultipleChoiceQuestionContainer canCreateOption={false} assessment={assessment} question={question as MultipleChoiceQuestion} onChange={handleQuestionChange} />
        )}

        {answerType === "text" && (
          <TextAnswer question={question as TextQuestion} onChange={handleQuestionChange} />
        )}
      </div>
      <div className={styles.actions}>
        {index > 0 &&
          <Tooltip content="Previous Question" placement="left">
            <button className={cx(styles.action, styles.previous)} onClick={handlePreviousClicked}>
              <i className="paperflite-left-arrow"></i>
            </button>
          </Tooltip>
        }

        {(index < _.size(assessment.questions) - 1) &&
          <Button className={styles.nextQuestion} variant="gradient" icon="paperflite-right-arrow" text="Next Question" onClick={handleNextClicked}></Button>
        }
        {(_.size(assessment.questions) <= 1) &&
          <Button className={styles.newQuestion} variant="gradient" icon="paperflite-right-arrow" text="Add New Question" onClick={onCreateClicked} />
        }
        {_.size(assessment.questions) > 1 &&
          <Tooltip content="Delete Question" placement="right">
            <button className={cx(styles.action, styles.delete)} onClick={() => handleDeleteQuestion()}>
              <i className="paperflite-delete"></i>
            </button>
          </Tooltip>
        }
      </div>
    </div>
  );
}

interface MultipleChoiceQuestionContainerProps {
  assessment: Assessment;
  question: MultipleChoiceQuestion;
  canCreateOption: boolean;
  // eslint-disable-next-line no-unused-vars
  onChange: (question: Question) => void;
}

const MultipleChoiceQuestionContainer: React.FC<MultipleChoiceQuestionContainerProps> = ({ assessment, question, canCreateOption, onChange }) => {

  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: {
      distance: 8
    }
  });

  const sensors = useSensors(
    pointerSensor
  );

  const options = useMemo(() => {
    return _.get(question, "options") || [];
  }, [question]);

  const answer = useMemo(() => {
    return _.get(question, "answer");
  }, [question]);

  const handleQuestionChange = (request: MultipleChoiceQuestion) => {
    onChange(request);
  }

  const handleOptionChange = (option: Option, index: number) => {
    const updatedOptions = _.map(options, (o, i) => {
      return i === index ? option : o;
    });

    handleQuestionChange({
      ...question,
      options: updatedOptions
    });
  }

  const handleAnswerChange = (value: string) => {
    handleQuestionChange({
      ...question,
      answer: value
    });
  }

  const handleCreateOption = () => {
    const option = {
      uuid: nanoid(6),
      name: "",
      order: _.size(options)
    };

    handleQuestionChange({
      ...question,
      options: [...options, option]
    });
  }

  const handleDeleteOption = (option: Option) => {
    const updatedOptions = _.filter(options, o => o.uuid !== option.uuid);
    handleQuestionChange({
      ...question,
      options: updatedOptions,
      answer: option.uuid === answer ? "" : answer
    });
  }

  const handleDragFinish = (event: any) => {
    const { active, over } = event;

    if (active?.id && over?.id && (active.id !== over.id)) {
      const source = active.data.current.entity;
      const target = over.data.current.entity;

      const sourceIndex = _.findIndex(options, (option) => option.uuid === source.uuid);
      const targetIndex = _.findIndex(options, (option) => option.uuid === target.uuid);

      const updatedOptions = _.map(options, (option, index) => {
        if (index === sourceIndex) {
          return target;
        }

        if (index === targetIndex) {
          return source;
        }

        return option;
      }).map((option, index) => {
        return {
          ...option,
          order: index
        }
      });

      handleQuestionChange({
        ...question,
        options: updatedOptions
      });
    }
  }

  return (
    <DndContext sensors={sensors} modifiers={[restrictToVerticalAxis]} onDragOver={handleDragFinish}>
      <div className={styles.multipleChoiceQuestionContainer}>
        <div className={styles.optionsContainer}>
          <SortableContext id={`question_${question.uuid}_options`} items={options.map(option => option.uuid)} strategy={verticalListSortingStrategy}>
            {_.map(options, (option, index) => (
              <OptionContainer key={index}
                assessment={assessment}
                question={question}
                option={option}
                selected={answer === option.uuid}
                canDelete={_.size(options) > 1 && canCreateOption}
                onChange={(option) => handleOptionChange(option, index)}
                onSelectionChange={(option) => handleAnswerChange(option.uuid)}
                onDelete={handleDeleteOption}
              />
            ))}
          </SortableContext>
          {canCreateOption && (
            <div className={styles.addOption} onClick={handleCreateOption}>
              <i className="paperflite-add"></i>Add new choice
            </div>
          )}
        </div>
      </div>
    </DndContext>
  );
}

interface OptionContainerProps {
  assessment: Assessment;
  question: MultipleChoiceQuestion;
  option: Option;
  selected: boolean;
  canDelete: boolean;
  // eslint-disable-next-line no-unused-vars
  onChange: (option: Option) => void;
  // eslint-disable-next-line no-unused-vars
  onSelectionChange: (option: Option) => void;
  // eslint-disable-next-line no-unused-vars
  onDelete: (option: Option) => void;
}

const OptionContainer: React.FC<OptionContainerProps> = ({ assessment, question, option, selected, canDelete, onChange, onSelectionChange, onDelete }) => {
  const name = useMemo(() => {
    return _.get(option, "name");
  }, [option]);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({
    id: option.uuid,
    disabled: false,
    data: {
      entity: option,
      type: 'option'
    }
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };


  const optionError = useSelector((state: any) => {
    const errors = _.get(state.courses, `edit.errors`) || [];
    const property = `questions.${question.uuid}.options.${option.uuid}.name`;
    return _.find(errors, { type: 'assessment', uuid: assessment.uuid, property: property });
  });

  const handleOptionChange = (request: Option) => {
    onChange(request);
  }

  const handleTitleChange = (e: any) => {
    handleOptionChange({
      ...option,
      name: e.target.value
    });
  }

  const handleDeleteOption = () => {
    onDelete(option);
  }

  const handleSelectClicked = () => {
    onSelectionChange(option);
  }

  return (
    <div className={cx(styles.optionContainer, {
      [styles.selected]: selected
    })} style={style} ref={setNodeRef}>
      <div className={styles.radio} onClick={handleSelectClicked}>
        <i className={selected ? "paperflite-radio-button-selected" : "paperflite-radio-button-unselected"}></i>
      </div>
      <input className={styles.optionText} placeholder="Enter a valid choice..." value={name} onChange={handleTitleChange} />
      <div className={styles.actions}>
        <Reveal show={optionError != null}>
          <Tooltip content="Choice cannot be empty" placement="top">
            <div className={cx(styles.action, styles.error)}>
              <i className="fa-solid fa-triangle-exclamation"></i>
            </div>
          </Tooltip>
        </Reveal>
        <Tooltip content="Drag to Rearrange" placement="top">
          <div className={styles.action} {...listeners} {...attributes} >
            <i className="paperflite-drag"></i>
          </div>
        </Tooltip>
        {canDelete &&
          <Tooltip content="Delete Option" placement="top">
            <button className={styles.action} onClick={handleDeleteOption}>
              <i className="paperflite-delete"></i>
            </button>
          </Tooltip>
        }
      </div>
    </div>
  );
}

interface TextAnswerProps {
  question: Question;
  // eslint-disable-next-line no-unused-vars
  onChange: (question: Question) => void;
}

const TextAnswer: React.FC<TextAnswerProps> = ({ question, onChange }) => {
  const answer = useMemo(() => {
    return _.get(question, "answer");
  }, [question]);

  const handleAnswerChange = (e: any) => {
    onChange({
      ...question,
      answer: e.target.value
    } as TextQuestion);
  }

  return (
    <TextareaAutosize className={styles.textAnswer}
      placeholder="Provide answer for the question..."
      rows={5}
      maxRows={10}
      value={answer}
      onChange={handleAnswerChange}
      onPointerEnterCapture={() => { }}
      onPointerLeaveCapture={() => { }}
    />
  );
}

interface SelectMenuProps {
  selected?: string | null;
  placeholder?: string;
  options: {
    icon?: string;
    label: string;
    value: string;
  }[];
  // eslint-disable-next-line no-unused-vars
  onChange?: (value: string) => void;
}

const SelectMenu: React.FC<SelectMenuProps> = ({ selected, placeholder, options, onChange }) => {
  const selectedOption = useMemo(() => {
    return options.find(option => option.value === selected);
  }, [selected, options]);

  const handleSelect = (value: string) => {
    if (onChange) {
      onChange(value);
    }
  }

  return (
    <Dropdown className={styles.selectDropdown}>
      <Dropdown.Toggle>
        {selectedOption && (
          <>
            {selectedOption?.icon && (
              <img src={selectedOption.icon} alt={selectedOption.label} />
            )}
            {selectedOption?.label}
          </>
        )}

        {!selectedOption && placeholder}
        {_.size(options) > 1 && <i className="paperflite-dropdown"></i>}
      </Dropdown.Toggle>

      {_.size(options) > 1 && (
        <Dropdown.Menu
          popperConfig={{
            modifiers: [
              {
                name: "offset",
                options: {
                  offset: [0, 6],
                },
              },
            ],
          }}
        >
          {options.map((option, index) => {
            const selected = option.value === selectedOption?.value;
            return (
              <Dropdown.Item
                key={index}
                className={cx({ [styles.active]: selected })}
                onClick={() => handleSelect(option.value)}
              >
                <span>{option.label}</span>
              </Dropdown.Item>
            );
          })}
        </Dropdown.Menu>
      )}
    </Dropdown>
  );

}